import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Col, Divider, Form, Input, Row, Space, Upload } from 'antd';
import { isArray } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import * as urlSlug from 'url-slug';
import { v4 as uuidv4 } from 'uuid';
import { AppContext } from '../../AppContext';
import {
  CREATE_WORKSPACE,
  UPDATE_WORKSPACE
} from '../../app/components/sidebar/graphql/Mutation';
import {
  GET_WORKSPACE,
  GET_WORKSPACES,
  GET_WORKSPACE_SIGNED_URL
} from '../../app/components/sidebar/graphql/Queries';
import {
  APP,
  DEFAULT_WEB_COLORS,
  MAX_LENGTHS,
  ROUTES
} from '../../common/constants';
import {
  Blurhash,
  fileUpload,
  formValidatorRules,
  getBase64
} from '../../common/utils';
import PageHeader from '../../components/PageHeader';
import PreviewModal from '../../components/PreviewModal';
import ProgressBar from '../../components/ProgressBar';
import useRedirectUser from '../../hooks/useRedirectUser';
import { SlugInput } from '../labels/topics/components/FormInputs';
import { Switch } from '../pages/component/pageModules/moduleForms/FormInputs';
import {
  DEFAULT_CONFIGS,
  DEFAULT_CONFIG_KEYS,
  MobileView,
  getConfigData,
  parseConfigData
} from './components';

const { TextArea } = Input;

function Configuration({ form }) {
  return (
    <div className="workspace-configs">
      {Object.entries(DEFAULT_CONFIGS)
        .filter(
          ([key]) =>
            ![
              DEFAULT_CONFIG_KEYS.FONTS,
              DEFAULT_CONFIG_KEYS.EMAIL_VERIFICATION_BASE_URL,
              DEFAULT_CONFIG_KEYS.APP_IMAGE_POSITION,
              DEFAULT_CONFIG_KEYS.APP_IMAGE_SIZE,
              DEFAULT_CONFIG_KEYS.DEFAULT_IMAGE,
              DEFAULT_CONFIG_KEYS.SHOW_MENU
            ].includes(key)
        )
        ?.map(([key, { component: Component, name }]) => (
          <Component key={key} form={form} namePath={['config', name]} />
        ))}
    </div>
  );
}

const initialValues = {
  description: '',
  isActive: true,
  name: '',
  slug: '/',
  owner: {
    firstName: '',
    lastName: '',
    email: ''
  },
  config: {
    emailVerificationBaseURL: '',
    colors: DEFAULT_WEB_COLORS
  }
};

export default function AddEditWorkspace({ history, match: { params } }) {
  const { id } = params;
  const isEdit = !!id;

  const {
    state: { workspace },
    dispatch,
    updateWorkspaceConfig
  } = useContext(AppContext);

  const [btnLoading, setBtnLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [imageProgress, setImageProgress] = useState(null);
  const [iconProgress, setIconProgress] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [form] = Form?.useForm();
  const imageArray = Form?.useWatch('image', form);
  const iconArray = Form?.useWatch('icon', form);
  const { redirectUser } = useRedirectUser();

  const [getSignedUrl, { loading }] = useLazyQuery(GET_WORKSPACE_SIGNED_URL, {
    onError: () => {
      setBtnLoading(false);
    }
  });

  useEffect(() => {
    form?.resetFields();
  }, [id, form]);

  const [
    getWorkspace,
    { data: workspaceData, loading: fetchingDetails }
  ] = useLazyQuery(GET_WORKSPACE, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const slug = res?.workspace?.slug.startsWith('/')
        ? res?.workspace?.slug
        : `/${res?.workspace?.slug}`;

      form?.setFieldsValue({
        ...res?.workspace,
        ...(res?.workspace?.image && {
          image: [{ url: res?.workspace?.image }]
        }),
        ...(res?.workspace?.favicon && {
          icon: [{ url: res?.workspace?.favicon }]
        }),
        slug,
        config: parseConfigData(res?.workspace?.config) || initialValues?.config
      });
    },
    onError: () => {}
  });

  const uploadButton = (
    <div>
      {btnLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div className="mt-8">Upload</div>
    </div>
  );

  useEffect(() => {
    const recordId = id;
    if (recordId) {
      getWorkspace({
        variables: {
          where: { id: recordId }
        }
      });
    }
  }, [id]);

  const [addUpdateWorkspace] = useMutation(
    isEdit ? UPDATE_WORKSPACE : CREATE_WORKSPACE,
    {
      onError: () => {
        setBtnLoading(false);
      }
    }
  );

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      // eslint-disable-next-line no-param-reassign
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewImage('');
    setPreviewOpen(false);
  };

  const handleSubmit = async (data) => {
    try {
      setBtnLoading(true);
      let contentType = null;
      const payload = {
        ...data,
        slug: data?.slug?.startsWith('/')
          ? data?.slug?.substring(1)
          : data?.slug,
        config: getConfigData(data?.config, [DEFAULT_CONFIG_KEYS.COLORS])
      };
      const currentImage = data?.image?.[0];
      if (data?.image?.length > 0 && !currentImage?.url) {
        const uuid = uuidv4();
        const imageObj = currentImage;
        const imageFile = imageObj?.originFileObj;
        const fileName = imageObj?.name;
        contentType = imageObj?.type;
        const { data: signUrlData, error } = await getSignedUrl({
          variables: { data: { contentType, fileName, uuid } }
        });
        if (error) return;
        const signedUrlData = signUrlData?.getWorkspaceImageUploadSignedUrl;
        await fileUpload(signedUrlData?.signedUrl, imageFile, setImageProgress);
        payload.blurhash = await Blurhash.encode(imageFile);
        if (!isEdit) {
          payload.uuid = uuid;
        }
        payload.imageKey = signedUrlData?.key;
        delete payload?.image;
      } else {
        payload.imageKey = null;
        payload.blurhash = null;
        delete payload?.image;
      }
      const currentIcon = data?.icon?.[0];
      if (data?.icon?.length > 0 && !currentIcon?.url) {
        const uuid = uuidv4();
        const iconObj = currentIcon;
        const iconFile = iconObj?.originFileObj;
        const fileName = iconObj?.name;
        contentType = iconObj?.type;
        const { data: signUrlData, error } = await getSignedUrl({
          variables: { data: { contentType, fileName, uuid } }
        });
        if (error) return;
        const signedUrlData = signUrlData?.getWorkspaceImageUploadSignedUrl;
        await fileUpload(signedUrlData?.signedUrl, iconFile, setIconProgress);
        payload.blurhash = await Blurhash.encode(iconFile);
        if (!isEdit) {
          payload.uuid = uuid;
        }
        payload.faviconKey = signedUrlData?.key;
        delete payload?.icon;
      } else {
        payload.faviconKey = null;
        delete payload?.icon;
      }
      if (currentImage?.url) {
        const editValue = workspaceData?.workspace;
        payload.blurhash = editValue?.blurhash;
        payload.imageKey = editValue?.imageKey;
        delete payload?.image;
      }
      if (currentIcon?.url) {
        const editValue = workspaceData?.workspace;
        payload.faviconKey = editValue?.faviconKey;
        delete payload?.image;
      }
      const recordId = id;
      const response = await addUpdateWorkspace({
        variables: {
          data: payload,
          ...(recordId && {
            where: { id: recordId }
          })
        },
        refetchQueries: [GET_WORKSPACES]
      });

      setIconProgress(null);
      setImageProgress(null);
      setBtnLoading(false);

      if (response?.data) {
        if (!recordId && response?.data?.createWorkspace?.workspace) {
          const {
            workspace: createdWorkspace
          } = response?.data?.createWorkspace;
          // eslint-disable-next-line no-undef
          localStorage?.removeItem(APP);
          dispatch({
            type: 'SET_WORKSPACE',
            data: {
              id: createdWorkspace?.id,
              label: createdWorkspace?.name,
              value: createdWorkspace?.uuid,
              config: createdWorkspace?.config
            }
          });
          dispatch({
            type: 'SET_USER_PERMISSIONS',
            data: createdWorkspace?.permissions || []
          });
          updateWorkspaceConfig(createdWorkspace?.config);
          history.push(ROUTES.VIDEOS_MODULE);
        } else {
          if (workspace?.id === id) {
            dispatch({
              type: 'SET_WORKSPACE',
              data: {
                id,
                value: workspaceData?.workspace?.uuid,
                label: data?.name
              }
            });
          }
          const getRoute = redirectUser();

          history?.push(getRoute);
        }
      }
    } catch (error) {
      setImageProgress(null);
      setIconProgress(null);
      setBtnLoading(false);
      return error;
    }
  };

  const handleCancel = () => {
    const getRoute = redirectUser();
    history?.push(getRoute);
  };

  const handleNameChange = (e) => {
    form?.setFieldValue('slug', `/${urlSlug.convert(e.target.value)}`);
  };

  return (
    <>
      <PreviewModal
        show={previewOpen}
        url={previewImage}
        onClose={handlePreviewClose}
      />
      <PageHeader noNavigation />
      <div className="page-wrapper workspace">
        <div className="page-wrapper-body">
          <Row>
            <Col sm={12}>
              <Form
                className="add-edit-form"
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={initialValues}
                disabled={loading || btnLoading}
              >
                <Form.Item
                  label="Workspace Name"
                  name="name"
                  required
                  rules={[
                    formValidatorRules?.required(
                      'Please enter workspace name!'
                    ),
                    formValidatorRules?.maxLength(MAX_LENGTHS?.NAME)
                  ]}
                >
                  <Input
                    placeholder="Enter workspace name"
                    onChange={handleNameChange}
                  />
                </Form.Item>
                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    formValidatorRules?.maxLength(MAX_LENGTHS.DESCRIPTION)
                  ]}
                >
                  <TextArea
                    rows={2}
                    placeholder="Enter description"
                    disabled={btnLoading}
                  />
                </Form.Item>
                <Form.Item
                  label="Slug"
                  name="slug"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter slug!'
                    },
                    formValidatorRules?.maxLength(MAX_LENGTHS.TITLE)
                  ]}
                >
                  <SlugInput />
                </Form.Item>
                <Form.Item
                  name="image"
                  label="Logo"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => {
                    if (isArray(e)) {
                      return e;
                    }
                    return e?.fileList;
                  }}
                  rules={[{ required: true, message: 'Please select logo!' }]}
                >
                  <Upload
                    name="avatar"
                    listType="picture-card"
                    className="avatar-uploader"
                    beforeUpload={() => false}
                    maxCount={1}
                    accept=".jpg,.jpeg,.png"
                    onPreview={handlePreview}
                  >
                    {imageArray?.length > 0 ? null : uploadButton}
                  </Upload>
                </Form.Item>
                {imageProgress && imageProgress >= 0 && (
                  <Form.Item>
                    <ProgressBar progress={imageProgress} />
                  </Form.Item>
                )}
                <Form.Item
                  name="icon"
                  label="Favicon"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => {
                    if (isArray(e)) {
                      return e;
                    }
                    return e?.fileList;
                  }}
                  rules={[
                    { required: true, message: 'Please select favicon icon!' }
                  ]}
                >
                  <Upload
                    name="avatar"
                    listType="picture-card"
                    className="avatar-uploader"
                    beforeUpload={() => false}
                    maxCount={1}
                    accept=".ico,.png"
                    onPreview={handlePreview}
                  >
                    {iconArray?.length > 0 ? null : uploadButton}
                  </Upload>
                </Form.Item>
                {iconProgress && iconProgress >= 0 && (
                  <Form.Item>
                    <ProgressBar progress={iconProgress} />
                  </Form.Item>
                )}
                <Form.Item
                  className="m-0"
                  name="isActive"
                  valuePropName="checked"
                >
                  <Switch label="Active" />
                </Form.Item>
                {!isEdit && (
                  <>
                    <Divider orientation="left">Owner Details</Divider>
                    <Form.Item name={['owner', 'firstName']} label="First Name">
                      <Input placeholder="Enter first name" />
                    </Form.Item>
                    <Form.Item name={['owner', 'lastName']} label="Last Name">
                      <Input placeholder="Enter last name" />
                    </Form.Item>
                    <Form.Item
                      name={['owner', 'email']}
                      label="Email"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter email!'
                        },
                        formValidatorRules?.email,
                        formValidatorRules?.maxLength(MAX_LENGTHS?.FORM_INPUT)
                      ]}
                    >
                      <Input type="email" placeholder="username@gmail.com" />
                    </Form.Item>
                  </>
                )}
                <Divider orientation="left">Configurations</Divider>
                <Configuration form={form} />

                <div className="d-flex button-section mb-8">
                  <Space>
                    <Button
                      disabled={loading || fetchingDetails}
                      loading={loading}
                      type="text"
                      htmlType="submit"
                      className="text-btn mr-8"
                      size="middle"
                    >
                      Save
                    </Button>

                    <Button
                      disabled={loading}
                      type="text"
                      className="text-btn2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Space>
                </div>
              </Form>
            </Col>
            <Col sm={12}>
              <MobileView form={form} namePath={['config', 'colors']} />
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}
